import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadSimple, Trash, File as FileIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, API_ORIGIN } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Media() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/media").then((r) => setAssets(r.data)).catch(() => {});
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media asset?")) return;
    try { await api.delete(`/media/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const url = (a, variant) => {
    let u = `${API_ORIGIN}${a.signed_url}`;
    if (variant) u += `&variant=${variant}`;
    return u;
  };

  return (
    <div>
      <PageHeader
        title={t("m2.mediaTitle")}
        subtitle={t("m2.mediaSub")}
        action={
          <>
            <input ref={inputRef} type="file" className="hidden" onChange={onFile} data-testid="media-file-input" />
            <Button className="rounded-sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="upload-media-button">
              <UploadSimple size={16} className="mr-2" />{uploading ? t("common.loading") : t("m2.upload")}
            </Button>
          </>
        }
      />

      {assets.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center text-muted-foreground" data-testid="media-empty">{t("m2.noMedia")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {assets.map((a) => {
            const isImg = a.content_type?.startsWith("image/");
            const hasThumb = a.variants?.some((v) => v.name === "thumbnail");
            return (
              <div key={a.id} className="bg-card border border-border rounded-sm overflow-hidden group" data-testid={`media-card-${a.id}`}>
                <div className="aspect-square bg-secondary flex items-center justify-center overflow-hidden">
                  {isImg
                    ? <img src={url(a, hasThumb ? "thumbnail" : null)} alt={a.filename} className="w-full h-full object-cover" />
                    : <FileIcon size={40} className="text-muted-foreground" />}
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate" title={a.filename}>{a.filename}</div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge variant="secondary" className="rounded-sm text-[10px] font-mono">{(a.size / 1024).toFixed(1)} KB</Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100" onClick={() => remove(a.id)} data-testid={`media-delete-${a.id}`}><Trash size={14} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
